import React, { useState, useEffect, useRef } from 'react';
import { Flex, Box, Button } from 'rebass';
import { FiChevronDown, FiCode } from 'react-icons/fi';
import NiceDropdown from 'components/NiceDropdown';
import { Portal } from 'components/Portal';
import { PositionerV2 } from 'components/Positioner';

/**
 * A button styled like a native select, with a NiceDropdown for its options.
 *
 * @param {Object} props
 * @param {boolean} [props.renderInPortal] Render the dropdown on `document.body`,
 *   anchored to the button, instead of absolutely inside the wrapper. Needed inside a
 *   NiceModal: the modal's content box is `overflow: hidden`, so an inline dropdown is
 *   clipped at the modal's edge. Portalling avoids the alternative of `overflow="visible"`
 *   on the modal, which would also remove the modal's own scrolling.
 */
function NiceSelect(props) {
    const [valueSelected, setValueSelected] = useState(props.defaultValue);
    const [showDropdown, setShowDropdown] = useState(false);
    const buttonRef = useRef(null);
    // `mimicSelectStyle` widens the dropdown to '100%', which resolves against the
    // relative wrapper inline but against <body> once portalled — so in a portal the
    // button's rendered width is measured and passed in pixels instead.
    const [buttonWidth, setButtonWidth] = useState(null);

    useEffect(() => {
        setValueSelected(props.defaultValue);
    }, [props.defaultValue]);

    useEffect(() => {
        if (!props.renderInPortal || !showDropdown || !buttonRef.current) return;

        setButtonWidth(buttonRef.current.getBoundingClientRect().width);
    }, [props.renderInPortal, showDropdown]);

    let buttonStyle = {
        //textAlign: 'left'
    };

    if (props.mimicSelectStyle) {
        buttonStyle.width = '100%';
        buttonStyle.fontWeight = 400;
        buttonStyle.padding = '0 12px';
    }

    let dropdownWidth = props.mimicSelectStyle ? '100%' : 'auto';
    if (props.renderInPortal && props.mimicSelectStyle) {
        dropdownWidth = buttonWidth ? `${buttonWidth}px` : 'auto';
    }

    const dropdown = (
        <NiceDropdown
            positionRight={props.positionRight ? props.positionRight : 'auto'}
            positionLeft={props.positionLeft ? props.positionLeft : 'auto'}
            width={dropdownWidth}
            maxWidth={props.renderInPortal && buttonWidth ? `${buttonWidth}px` : props.maxWidth}
            revealEllipsis={props.revealEllipsis ? true : false}
            showSearch={props.showSearch ? true : false}
            onClose={() => setShowDropdown(false)}
            items={props.items}
            value={valueSelected}
            onChange={value => {
                setValueSelected(value);
                props.onChange(value);
            }}
            // Inline, the dropdown offsets itself from the wrapper and sizes its own
            // height. Portalled, PositionerV2 owns both, so the dropdown is taken out of
            // the flow (`position: static`, no inset) and `adjustHeight` is dropped —
            // the two would otherwise fight over maxHeight on scroll.
            style={props.renderInPortal ? { inset: 'auto', position: 'static' } : undefined}
            adjustHeight={props.renderInPortal ? undefined : 1}
        />
    );

    return (
        <Box sx={{ position: 'relative' }}>
            <Button
                ref={buttonRef}
                mr={0}
                sx={buttonStyle}
                type="button"
                variant={props.variant ? props.variant : 'secondary-gray'}
                onClick={() => setShowDropdown(true)}
                className="dropdown"
            >
                <Flex justifyContent={'space-between'}>
                    <Box>
                        {props.defaultLabel ? (
                            <span className="text-secondary normal">{props.defaultLabel}: </span>
                        ) : (
                            ''
                        )}
                        {valueSelected != undefined && props.mapValueToLabel
                            ? props.mapValueToLabel[valueSelected]
                            : 'None'}
                    </Box>
                    <Box>
                        <FiChevronDown
                            style={{
                                marginRight: 0,
                                marginLeft: '4px',
                                marginTop: '4px'
                                //rotate: '90deg',
                                //fontSize: '13px'
                            }}
                        />
                    </Box>
                </Flex>
            </Button>
            {showDropdown &&
                (props.renderInPortal ? (
                    <Portal>
                        <PositionerV2 anchorRef={buttonRef}>{dropdown}</PositionerV2>
                    </Portal>
                ) : (
                    dropdown
                ))}
        </Box>
    );
}

export default NiceSelect;
